"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import group9 from "./group-9.png";
import group11 from "./group-11.png";
import image1 from "./image-1.png";
import image3 from "./image-3.png";

const testimonials = [
  {
    id: 1,
    quote: "Absolutely stunning and delicious! 🎂 The cake not only looked like a masterpiece but tasted heavenly too — perfectly balanced flavors with a smooth creamy texture and the crunch on the sides added the perfect touch. Truly a showstopper for any celebration!",
    name: "Savannah Nguyen",
    role: "Customer",
    avatar: "https://api.builder.io/api/v1/image/assets/2a838274f46548a0b8b8310254100157/ae82ba44756762680c2b08c5d7a00da928e1e580?placeholderIfAbsent=true",
    rating: "https://cdn.acsdev.in/iynacakes/group-9.png"
  },
  {
    id: 2,
    quote: "The Strawberry Cake was an absolute delight! Fresh, fruity, and irresistibly soft — every bite was filled with the natural sweetness of strawberries and smooth cream. It not only looked stunning but tasted like pure happiness in a slice!",
    name: "Marcus Johnson",
    role: "Business Owner",
    avatar: "https://api.builder.io/api/v1/image/assets/2a838274f46548a0b8b8310254100157/ae82ba44756762680c2b08c5d7a00da928e1e580?placeholderIfAbsent=true",
    rating: "https://cdn.acsdev.in/iynacakes/group-9.png"
  },
  {
    id: 3,
    quote: "A heavenly treat for berry lovers! The Blueberry Cake had the perfect blend of sweetness and tang, with a rich creamy texture that melted in the mouth. The burst of blueberry flavor made it both refreshing and luxurious — simply unforgettable!",
    name: "Emily Rodriguez",
    role: "Project Manager",
    avatar: "https://api.builder.io/api/v1/image/assets/2a838274f46548a0b8b8310254100157/ae82ba44756762680c2b08c5d7a00da928e1e580?placeholderIfAbsent=true",
    rating: "https://cdn.acsdev.in/iynacakes/group-9.png"
  }
];

// Lazy Image Component with loading state
const LazyImage = ({ src, alt, className, onLoad, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  }, [onLoad]);

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {!isLoaded && (
        <div className={`absolute inset-0 bg-gray-200 animate-pulse rounded ${className}`} />
      )}
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          loading="lazy"
          {...props}
        />
      )}
    </div>
  );
};

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef();

  // Intersection Observer for component visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-play functionality - only start when component is visible
  useEffect(() => {
    if (!isAutoPlaying || !isVisible) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isVisible]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDotClick(index);
    }
  };

  // Animation variants for biscuits with center-to-sides movement and bounce
  const leftBiscuitVariants = {
    initial: {
      x: "calc(50vw - 200px)",
      y: "calc(50vh - 298px)",
      opacity: 0,
      scale: 0.5
    },
    animate: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.5,
        ease: "easeOut",
        delay: 0.3
      }
    }
  };

  const rightBiscuitVariants = {
    initial: {
      x: "calc(-50vw + 200px)",
      y: "calc(50vh - 298px)",
      opacity: 0,
      scale: 0.5
    },
    animate: {
      x: 0,
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.5,
        ease: "easeOut",
        delay: 0.5
      }
    }
  };

  // Continuous bounce animation
  const bounceVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 1
      }
    }
  };

  const testimonialVariants = {
    initial: { opacity: 0, x: 100 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      x: -100,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-[596px] bg-orange-50 overflow-hidden flex items-center justify-center"
      aria-label="Customer testimonials"
      role="region"
    >
      {/* Left biscuit image - positioned to show 25-30% on screen */}
      {isVisible && (
        <motion.div
          className="absolute top-[55px] hidden lg:block z-0 lg:-left-[20%] xl:-left-[18%] 2xl:-left-[15%]"
          style={{
            left: 'clamp(-75%, -20vw, -15%)'
          }}
          variants={leftBiscuitVariants}
          initial="initial"
          animate="animate"
        >
          <motion.div variants={bounceVariants} animate="animate">
            <LazyImage
              src="https://cdn.acsdev.in/iynacakes/image-1.png"
              className="h-[486px] w-auto object-contain"
              alt="Decorative biscuit illustration"
            />
          </motion.div>
        </motion.div>
      )}

      {/* Right biscuit image - positioned to show 25-30% on screen */}
      {isVisible && (
        <motion.div
          className="absolute top-[55px] hidden lg:block z-0 lg:-right-[20%] xl:-right-[18%] 2xl:-right-[15%]"
          style={{
            right: 'clamp(-75%, -20vw, -15%)'
          }}
          variants={rightBiscuitVariants}
          initial="initial"
          animate="animate"
        >
          <motion.div variants={bounceVariants} animate="animate">
            <LazyImage
              src="https://cdn.acsdev.in/iynacakes/image-3.png"
              className="h-[486px] w-auto object-contain"
              alt="Decorative biscuit illustration"
            />
          </motion.div>
        </motion.div>
      )}

      {/* Main testimonial card - centered using flexbox */}
      {isVisible && (
        <motion.div
          className="w-[923px] h-[486px] max-w-[95vw] max-h-[90vh] z-10 mx-2 sm:mx-4"
          variants={cardVariants}
          initial="initial"
          animate="animate"
        >
          <div className="relative w-full h-full bg-white rounded-xl shadow-[0px_10px_21px_rgba(230,57,70,0.15)] overflow-hidden">
            <div className="absolute w-full h-[312px] top-0 left-0">
              <div className="absolute w-full h-[278px] top-0 left-0 bg-white rounded-xl shadow-[0px_10px_21px_rgba(230,57,70,0.15)]" />

              {/* Quote marks */}
              <div className="absolute top-[62px] left-[104px] w-[37px] h-9 hidden md:block">
                <LazyImage
                  src="https://api.builder.io/api/v1/image/assets/2a838274f46548a0b8b8310254100157/092f14cef1a3033a2ff6d58dae1a95e72050ba87?placeholderIfAbsent=true"
                  alt=""
                  className="w-full h-full object-contain"
                  aria-hidden="true"
                />
              </div>

              {/* Testimonial content with animation */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  variants={testimonialVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="absolute top-16 left-4 right-4 md:left-[141px] md:right-[141px] md:top-20 flex justify-center"
                >
                  <blockquote className="font-normal text-slate-500 text-xs sm:text-sm md:text-base text-center tracking-wide leading-4 sm:leading-5 md:leading-6 max-w-full">
                    "{testimonials[currentIndex].quote}"
                  </blockquote>
                </motion.div>
              </AnimatePresence>

              {/* Customer avatar */}
              <div className="absolute w-full top-[220px] sm:top-[244px] flex justify-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`avatar-${currentIndex}`}
                    variants={testimonialVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="w-[68px] h-[68px] rounded-full overflow-hidden"
                  >
                    <LazyImage
                      src={testimonials[currentIndex].avatar}
                      className="w-full h-full object-cover"
                      alt={`${testimonials[currentIndex].name} profile picture`}
                    />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Star rating */}
            <div className="absolute w-full top-[304px] sm:top-[328px] flex justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`rating-${currentIndex}`}
                  variants={testimonialVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="w-[120px] sm:w-[152px] h-5 sm:h-6"
                >
                  <LazyImage
                    src={testimonials[currentIndex].rating}
                    className="w-full h-full object-contain"
                    alt="5 star rating"
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Customer info */}
            <div className="absolute w-full top-[329px] sm:top-[353px] flex justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`info-${currentIndex}`}
                  variants={testimonialVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="text-center"
                >
                  <div className="font-bold text-gray-800 text-sm md:text-base mb-1">
                    {testimonials[currentIndex].name}
                  </div>
                  <div className="font-medium text-gray-800 text-xs sm:text-sm md:text-base">
                    {testimonials[currentIndex].role}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation dots */}
            <div
              className="inline-flex items-start gap-1 absolute top-[420px] sm:top-[462px] left-1/2 transform -translate-x-1/2"
              role="tablist"
              aria-label="Testimonial navigation"
            >
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-controls={`testimonial-${index}`}
                  aria-label={`View testimonial ${index + 1} of ${testimonials.length}`}
                  tabIndex={index === currentIndex ? 0 : -1}
                  className={`relative w-[70px] h-1 rounded-[11px] border border-solid border-black border-opacity-20 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 ${
                    index === currentIndex
                      ? 'bg-gradient-to-r from-orange-500 to-teal-600'
                      : 'bg-orange-100 bg-opacity-50 hover:bg-opacity-75'
                  }`}
                  onClick={() => handleDotClick(index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Loading skeleton when component is not visible */}
      {!isVisible && (
        <div className="w-[923px] h-[486px] max-w-[95vw] max-h-[90vh] z-10 mx-2 sm:mx-4">
          <div className="relative w-full h-full bg-gray-200 rounded-xl animate-pulse">
            <div className="absolute top-16 left-4 right-4 md:left-[141px] md:right-[141px] md:top-20">
              <div className="h-20 bg-gray-300 rounded animate-pulse"></div>
            </div>
            <div className="absolute top-[244px] left-1/2 transform -translate-x-1/2 w-[68px] h-[68px] bg-gray-300 rounded-full animate-pulse"></div>
            <div className="absolute top-[328px] left-1/2 transform -translate-x-1/2 w-[152px] h-6 bg-gray-300 rounded animate-pulse"></div>
            <div className="absolute top-[353px] left-1/2 transform -translate-x-1/2 w-32 h-12 bg-gray-300 rounded animate-pulse"></div>
          </div>
        </div>
      )}

      {/* Screen reader content for current testimonial */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        <div id={`testimonial-${currentIndex}`}>
          Testimonial {currentIndex + 1} of {testimonials.length}:
          {testimonials[currentIndex].quote}
          - {testimonials[currentIndex].name}, {testimonials[currentIndex].role}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
